import React, { useEffect, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import Peer from "simple-peer";
import { createPortal } from "react-dom";
import { Mic, MicOff, LogOut, MessageSquare, Play, Globe, MonitorUp, MonitorOff, PlusCircle, Users, X, SportShoe, EyeOff, Maximize2, Minimize2, SquareArrowRightEnter, PhoneCall,
    Settings, UserPlus, UserMinus, Ban, Volume2, ShieldBan, Send, SendHorizontal, Mail, Camera, Edit, ArrowBigRightDash } from 'lucide-react';

import { Buffer } from 'buffer';
window.Buffer = Buffer;
const socket = io('http://localhost:5000', {
    transports: ['websocket']
});

const iceConfig = {
  iceServers: [
    {
      urls: [
        "stun:118.179.151.221:3478",
        "turn:118.179.151.221:3478?transport=tcp"
      ], 
      username: "myturnsrv1", 
      credential: "240215240215"
    }
  ]
};


const Avatar = ({ 
    stream, isMuted, name, color, isSelf, remoteMuted, 
    avatarUrl, isSharing, isViewing, onJoinScreen, onHideScreen,
    viewers = [], onMenuOpen, participantID, connectionStatus,
    status, messages

}) => {
    const [talking, setTalking] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0); 
    const [isFull, setIsFull] = useState(false); 
    const [volume, setVolume] = useState(1);
    const muted = isSelf ? isMuted : remoteMuted;
    const audioRef = useRef();
    const videoRef = useRef();

    const showVideo = isSharing && (isSelf || isViewing);

   // 1. Create a stable ref at the top of your Avatar component
const portalVideoRef = useRef(null);

// 2. Manage the stream assignment via Effect
useEffect(() => {
    // Only assign if the portal is open and the ref is ready
    if (isFull && portalVideoRef.current && stream) {
        // Only assign if the stream is DIFFERENT to prevent resetting/blinking
        if (portalVideoRef.current.srcObject !== stream) {
            portalVideoRef.current.srcObject = stream;
        }
    }
}, [isFull, stream]); // Only re-run if full-screen toggles or stream changes
   

    // FIX: Moved ESC key listener to the top level to follow Rules of Hooks
    useEffect(() => {
        if (!isFull) return;
        const handleEsc = (e) => { if (e.key === 'Escape') setIsFull(false); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isFull]);

    useEffect(() => {
        if (stream) {
            if (!isSelf && audioRef.current) {
                audioRef.current.srcObject = stream;
                audioRef.current.volume = volume;
            }

            if (showVideo && videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play().catch(err => console.warn(err));
            }
        }
        if (!isSharing) setIsFull(false);
    }, [stream, isSelf, showVideo, isSharing, volume]);
    

    useEffect(() => {
        if (!stream || muted) { 
            setTalking(false); setAudioLevel(0); return; 
        }
        let animationFrame;
        let actx;
        try {
            actx = new (window.AudioContext || window.webkitAudioContext)();
            const analyzer = actx.createAnalyser();
            const src = actx.createMediaStreamSource(stream);
            analyzer.fftSize = 64;
            src.connect(analyzer);
            const data = new Uint8Array(analyzer.frequencyBinCount);
            const check = () => {
                analyzer.getByteFrequencyData(data);
                const avg = data.reduce((a, b) => a + b) / data.length;
                setTalking(avg > 15); setAudioLevel(avg); 
                animationFrame = requestAnimationFrame(check);
            };
            check();
        } catch (e) {}
        return () => { 
            if(animationFrame) cancelAnimationFrame(animationFrame); 
            if(actx) actx.close(); 
        };
    }, [stream, muted]);
    
const renderConnectionLabel = () => {
    if (!connectionStatus) return null;
    
    const colors = {
      connecting: "#EAB308", // Yellow
      connected: "#22c55e",  // Green
      disconnected: "#ef4444" // Red
    };

    return (
      <div style={{ fontSize: '11px', color: colors[connectionStatus], marginTop: '2px' }}>
        {connectionStatus.toUpperCase()}
      </div>
    );
  };

    
    const renderStatusBubble = () => {
    if (!status) return null;
    return (
        <div style={{
            position: 'absolute',
            top: '-32px', // Moved up slightly to breathe
            right: '-12px',
            background: 'linear-gradient(135deg, #6c2dff 0%, #4b19b2 100%)', // Added gradient
            color: 'white',
            padding: '4px 14px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '700',
            zIndex: 60,
            boxShadow: '0 6px 16px rgba(0,0,0,0.5)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            border: '1.5px solid rgba(255,255,255,0.3)',
            // Floating Animation
            animation: 'bubbleFloat 3s ease-in-out infinite',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {status}
            
            {/* Bubble Tail - Refined for better alignment */}
            <div style={{
                position: 'absolute',
                bottom: '-7px',
                left: '20px',
                width: 0,
                height: 0,
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderTop: '9px solid #4b19b2' // Matches the bottom of the gradient
            }} />

            {/* Injected CSS for the animation */}
            <style>{`
                @keyframes bubbleFloat {
                    0% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-6px) rotate(2deg); }
                    100% { transform: translateY(0px) rotate(0deg); }
                }
            `}</style>
        </div>
    );
};

    const renderEqualizerArc = () => {
        if (!talking || showVideo) return null;
        const radius = 56.3; 
        const center = 75; 
        const arcLength = Math.min(359.99, (audioLevel / 110) * 360); 
        const endRad = (Math.PI / 180) * (120 + arcLength);
        const startRad = (Math.PI / 180) * 120;
        const x1 = center + radius * Math.cos(startRad); 
        const y1 = center + radius * Math.sin(startRad);
        const x2 = center + radius * Math.cos(endRad); 
        const y2 = center + radius * Math.sin(endRad);
        const pathData = `M ${x1} ${y1} A ${radius} ${radius} 0 ${arcLength > 180 ? 1 : 0} 1 ${x2} ${y2}`;

        return (
            <svg viewBox="0 0 150 150" style={{ position: 'absolute', width: '150px', height: '150px', pointerEvents: 'none', zIndex: 5 }}>
                <linearGradient id="arcGradient" x1="100%" y1="100%" x2="0%" y2="0%">
                        <stop offset="0%" stopColor="#ff1111" />
                        <stop offset="20%" stopColor="#d400ff" />
                        <stop offset="50%" stopColor="#00ff08" />
                        <stop offset="70%" stopColor="#fffb00" />
                        <stop offset="100%" stopColor="#ff1111" />
                    </linearGradient>
                <path d={pathData} fill="none" stroke="url(#arcGradient)" strokeWidth="5" strokeLinecap="round" />
            </svg>
        );
    };

    return (
            <div style={{ 
                textAlign: 'center', 
                transition: '0.3s ease', 
                gridColumn: (showVideo && isFull) ? '1 / -1' : 'auto', 
                width: '100%',
                zIndex: isFull ? 100 : 1 
            }}>
                {!isSelf && <audio ref={audioRef} autoPlay playsInline />}
                <div style={{
                    width: (showVideo && isFull) ? '100%' : (showVideo ? '320px' : '110px'), 
                    maxWidth: '100%',
                    height: (showVideo && isFull) ? '80vh' : (showVideo ? '180px' : '110px'), 
                    aspectRatio: (showVideo && isFull) ? '16/9' : 'unset',
                    borderRadius: showVideo ? '12px' : '50%', 
                    backgroundImage: (!showVideo && avatarUrl) ? `url(${avatarUrl})` : 'none',
                    backgroundColor: (!showVideo && !avatarUrl) ? color : '#000',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: isSharing ? '4px solid #34B7F1' : (talking && !showVideo ? '3px solid transparent' : '3px solid #3B4A54'),
                    boxShadow: isSharing ? '0 0 25px rgba(52, 183, 241, 0.5)' : 'none',
                    position: 'relative', 
                    overflow: showVideo ? 'hidden' : 'visible', 
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    {renderStatusBubble()}
                    {renderEqualizerArc()}
    
                    {!isSelf && (
                        <div 
                            onClick={() => onMenuOpen(participantID)} 
                            style={ !isSharing && !isFull ? 
                                { position: 'relative', top: '-50px', left: '60px', cursor: 'pointer', zIndex: 10, background: 'rgba(0,0,0,0.4)', borderRadius: '50%', padding: '5px', display: 'flex' } : 
                                { position: 'absolute', top: '10px', left: '10px', cursor: 'pointer', zIndex: 30, background: 'rgba(0,0,0,0.4)', borderRadius: '50%', padding: '5px', display: 'flex' }
                            }
                        >
                            <Settings size={16} color="#FFFFFF" />
                        </div>
                    )}
    
                    {!isSelf && (
                        <div style={{
                            position: 'absolute', bottom: isFull ? '20px' : '-22px', left: '50%', transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.6)', borderRadius: '10px', padding: ' 3px 10px',
                            display: 'flex', alignItems: 'center', gap: '10px', zIndex: 30
                        }}>
                            <Volume2 size={12} color="#FFFFFF" />
                            <input 
                                type="range" min="0" max="1" step="0.1" 
                                value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} 
                                style={{ width: isFull ? '120px' : '80px', height: '3px', cursor: 'pointer', accentColor: '#6c2dff' }} 
                            />
                        </div>
                    )}
    
                    {isSharing && !isSelf && !isViewing && (
                        <div onClick={onJoinScreen} style={styles.viewOverlay}>
                            <div style={styles.playCircle}><Play fill="#fff" size={24} /></div>
                            <p style={{ marginTop: '8px', fontSize: '11px', fontWeight: 'bold', color: '#fff' }}>CLICK TO VIEW</p>
                        </div>
                    )}
    
                    {showVideo ? (
                        <>
                            <video 
                                ref={videoRef} 
                                autoPlay 
                                playsInline 
                                muted={isSelf}
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: isFull ? 'contain' : 'cover', 
                                    borderRadius: '12px',
                                    background: '#000'
                                }} 
                            />
                            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: '8px', zIndex: 40 }}>
                                {!isSelf && <button onClick={onHideScreen} style={styles.videoIconBtn} title="Hide Screen"><EyeOff size={18} /></button>}
                                <button onClick={() => setIsFull(!isFull)} style={styles.videoIconBtn}>
                                    {isFull ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                                </button>
                            </div>
                        </>
                    ) : ( 
                        !avatarUrl && <span style={{ fontSize: '38px', color: '#fff', fontWeight: 'bold', zIndex: 2 }}>{name ? name[0].toUpperCase() : "?"}</span> 
                    )}
                </div>
    
                <div style={{ fontWeight: '700', fontSize: isFull ? '18px' : '14px', color: talking ? '#48ff00' : '#E9EDEF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '22px', gap: '6px' }}>
                    {name} {isSelf && "(You)"}  {renderConnectionLabel()}
                    {muted && <MicOff size={isFull ? 18 : 14} color="#fd0000" />}
                </div>
    
    
    {isFull && createPortal(
        <div style={{ 
            position: 'fixed', inset: 0, zIndex: 9999, 
            backgroundColor: '#000', // Use solid black; blur(10px) can cause lag/blinking
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            <video 
                ref={portalVideoRef} // Use the stable ref
                autoPlay 
                playsInline 
                muted={isSelf} // Crucial to avoid echo
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
            <button 
                onClick={() => setIsFull(false)} 
                style={{ position: 'absolute', top: 30, right: 30, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '50px', height: '50px', cursor: 'pointer', color: 'white' }}
            >
                <X size={28} />
            </button>
            <div style={{ position: 'absolute', top: 20, left: 20, color: 'white', background: 'rgba(0,0,0,0.5)', padding: '8px 20px', borderRadius: '20px' }}>
                Viewing: {name}'s Screen
            </div>
        </div>,
        document.body
    )}
            </div>
        );
    };


const RemoteParticipant = ({ peer, name, remoteMuted, avatarUrl, isSharing, participantID, currentRoom, viewers, status,messages, onMenuOpen, connectionStatus }) => {
    const [stream, setStream] = useState(null);
    const [isViewing, setIsViewing] = useState(false);
    useEffect(() => { 
        const handler = s => setStream(s);
        peer.on("stream", handler);
        return () => peer.off("stream", handler);
    }, [peer]);
    useEffect(() => { if (!isSharing) setIsViewing(false); }, [isSharing]);

    const handleToggleView = (status) => {
        setIsViewing(status);
        socket.emit("toggle_view_status", currentRoom, participantID, status);
    };

    return (
        <div style={styles.participantSlot}>
            <Avatar 
                name={name} stream={stream} avatarUrl={avatarUrl} color="#34B7F1" remoteMuted={remoteMuted} 
                isSharing={isSharing} isViewing={isViewing} viewers={viewers}
                participantID={participantID}
                onJoinScreen={() => handleToggleView(true)} onHideScreen={() => handleToggleView(false)}
                status={status}
                onMenuOpen={onMenuOpen}
                connectionStatus={connectionStatus}
                messages={messages}
            />
        </div>
    );
};

const ChatSidebar = ({ messages, onSendMessage, onClose, currentUserName, isPrivate, targetName }) => {
    const [text, setText] = useState("");

    const handleSend = () => {
        if (text.trim()) {
            onSendMessage(text);
            setText("");
        }
    };

    return (
        <div style={styles.chatSidebar}>
            <div style={styles.chatHeader}>
                <span style={{ fontWeight: 'bold' }}>{isPrivate ? `Chat: ${targetName}` : "Group Chat"}</span>
                <X onClick={onClose} style={{ cursor: 'pointer' }} size={20} />
            </div>

            <div style={{ ...styles.messageList, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                {messages.map((m, i) => {
                    const isOwn = m.senderName === currentUserName;
                    return (
                        <div key={i} style={{ 
                            ...styles.msgBubble, 
                            alignSelf: isOwn ? 'flex-end' : 'flex-start', 
                            background: isOwn ? '#6c2dff' : '#3b474e',
                            marginBottom: '8px'
                        }}>
                            {!isOwn && <div style={{ ...styles.msgSender, fontWeight: 'bold', fontSize: '12px', color: '#8696a0' }}>{m.senderName}</div>}
                            <div style={{ fontSize: '14px', color: '#E9EDEF', wordBreak: 'break-word' }}>{m.text}</div>
                            <div style={{ ...styles.msgTime, fontSize: '10px', textAlign: 'right', marginTop: '4px', opacity: 0.7 }}>{m.time}</div>
                        </div>
                    );
                })}
            </div>

            <div style={styles.chatInputArea}>
                <input 
                    style={styles.chatInput} 
                    placeholder="Type a message" 
                    value={text} 
                    onChange={e => setText(e.target.value)} 
                    onKeyDown={e => e.key === 'Enter' && handleSend()} 
                />
                <button onClick={handleSend} style={styles.sendBtn}><SendHorizontal size={20}/></button>
            </div>
        </div>
    );
};
    

// --- MAIN APP ---
export default function App() {
    const [user, setUser] = useState(null);
    const [authMode, setAuthMode] = useState("login"); 
    const [emailData, setEmailData] = useState({ email: "", password: "", fullName: "" });
    const [rooms, setRooms] = useState([]);
    const [peers, setPeers] = useState([]);
    const [inRoom, setInRoom] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [currentRoom, setCurrentRoom] = useState(null);
    const [showChat, setShowChat] = useState(false);
    const [privateChatTarget, setPrivateChatTarget] = useState(null); // Private messaging state
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [viewingMap, setViewingMap] = useState({});
    const [lobby, setLobby] = useState([]); 
    const [showModal, setShowModal] = useState(false);// eslint-disable-next-line
    const [selectedUser, setSelectedUser] = useState(null); 
    const [roomFormData, setRoomFormData] = useState({ room_name: "", language: "", max_participants: "" });

    // Add this near your other useState declarations
    const [otpCode, setOtpCode] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false); // Loading Modal state
    const [isCodeVerified, setIsCodeVerified] = useState(false); // Controls the step switch

    // Profile Modal
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

      const [isTransferOpen, setIsTransferOpen] = useState(false);
    
    // Feature States
    const [following, setFollowing] = useState([]); // eslint-disable-next-line
    const [blocked, setBlocked] = useState([]);
    const [activeMenu, setActiveMenu] = useState(null);

    const userStream = useRef();
    const screenStream = useRef();
    const peersRef = useRef([]);
    

    const leaveTable = useCallback(() => {
        socket.emit("leave_room", currentRoom);
        if (userStream.current) userStream.current.getTracks().forEach(t => t.stop());
        if (screenStream.current) screenStream.current.getTracks().forEach(t => t.stop());
        peersRef.current.forEach(p => { if (p.peer && !p.peer.destroyed) p.peer.destroy(); });
        peersRef.current = [];
        setPeers([]); setInRoom(false); setIsSharing(false); setViewingMap({});
        window.location.reload();
    }, [currentRoom]);

    const createPeer = useCallback((userToSignal, callerID, stream) => {
        const peer = new Peer({ initiator: true, trickle: false, stream, config: iceConfig });
        peer.on("signal", signal => {
            socket.emit("sending_signal", { userToSignal, callerID, signal, fullName: user?.fullName, avatar: user?.avatar_url });
        });
        return peer;
    }, [user]);

    const addPeer = useCallback((incomingSignal, callerID, stream) => {
        const peer = new Peer({  initiator: false, trickle: false, stream, config: iceConfig });
        peer.on("signal", signal => {
            socket.emit("returning_signal", { signal, callerID });
        });
        if (incomingSignal) { try { peer.signal(incomingSignal); } catch (e) {} }
        return peer;
    }, []);

    useEffect(() => {
    const handleStatusUpdate = ({ userID, status }) => {
        console.log("Status update received for:", userID, status); // Debug log
        setPeers(prevPeers => prevPeers.map(peer => {
            if (peer.id === userID) {
                return { ...peer, status: status };
            }
            return peer;
        }));
    };

    socket.on("status_changed", handleStatusUpdate);
    return () => socket.off("status_changed", handleStatusUpdate);
}, []);




    useEffect(() => {
        const savedUser = localStorage.getItem("bt_user_session");
        if (savedUser) { 
            setUser(JSON.parse(savedUser)); 
            setAuthMode("authenticated"); 
        }
        fetch('http://localhost:5000/api/rooms').then(r => r.json()).then(setRooms).catch(e => console.error(e));
        socket.on("update_lobby", (data) => setLobby(data));
        socket.on("user_joined", payload => {
            const existing = peersRef.current.find(p => p.peerID === payload.callerID);
            if (!existing) {
                const peer = addPeer(payload.signal, payload.callerID, userStream.current);
                peersRef.current.push({ peerID: payload.callerID, peer });
                setPeers(prev => [...prev, { peer, name: payload.fullName, id: payload.callerID, avatar: payload.avatar, connectionStatus: "connecting", status: payload.status, remoteMuted: false, isSharing: false }]);
            // 3. Listen for the actual connection success
        peer.on("connect", () => {
            console.log("WebRTC Connected with:", payload.fullName);
            setPeers(prev => prev.map(p => 
                p.socketId === payload.callerID 
                ? { ...p, connectionStatus: "connected" } 
                : p
            ));
        });
            } else if (payload.signal && !existing.peer.destroyed) { 
                try { existing.peer.signal(payload.signal); } catch (e) {} 
            }
        });
        socket.on("receiving_returned_signal", p => {
            const item = peersRef.current.find(i => i.peerID === p.id);
            if (item && !item.peer.destroyed) { try { item.peer.signal(p.signal); } catch (e) {} }
        });
        socket.on("user_left", id => {
            setPeers(prev => prev.map(p => p.id === id ? { ...p, connectionStatus: "disconnected" } : p));
            const pObj = peersRef.current.find(x => x.peerID === id);
            if (pObj && !pObj.peer.destroyed) pObj.peer.destroy();
            peersRef.current = peersRef.current.filter(x => x.peerID !== id);
            setPeers(prev => prev.filter(x => x.id !== id));
            setViewingMap(prev => { const next = {...prev}; delete next[id]; return next; });
        });
        socket.on("peer_mute_change", ({ id, isMuted }) => {
            setPeers(prev => prev.map(p => p.id === id ? { ...p, remoteMuted: isMuted } : p));
        });
        socket.on("peer_screen_share_change", ({ id, isSharing }) => {
            setPeers(prev => prev.map(p => p.id === id ? { ...p, isSharing } : p));
            if (!isSharing) setViewingMap(prev => { const next = {...prev}; delete next[id]; return next; });
        });
        socket.on("peer_view_status_change", ({ viewerName, targetID, isViewing }) => {
            setViewingMap(prev => {
                const currentList = prev[targetID] || [];
                if (isViewing) return { ...prev, [targetID]: [...new Set([...currentList, viewerName])] };
                return { ...prev, [targetID]: currentList.filter(n => n !== viewerName) };
            });
        });
        socket.on("force_mute", () => {
            if (userStream.current) {
                userStream.current.getAudioTracks()[0].enabled = false;
                setIsMuted(true);
                socket.emit("toggle_mute", currentRoom, true);
            }
        });
        socket.on("you_are_kicked", () => {
            alert("You have been kicked from the room.");
            leaveTable();
            window.location.reload(); 
        });
        socket.on("receive_message", data => {
            setMessages(prev => [...prev, data]);
            if (!showChat) setUnreadCount(c => c + 1);
        });
        // 2. NEW: Private Message Listener
    socket.on("receive_private_message", data => {
        // We add the private message to the same messages state
        // but it will have the isPrivate flag or receiverID to filter it correctly later
        setMessages(prev => [...prev, { ...data, isPrivate: true }]);
        
        // If chat isn't open, increment unread count so the user knows
        if (!showChat) setUnreadCount(c => c + 1);
    });

        return () => {
            socket.off("update_lobby"); socket.off("user_joined"); socket.off("receiving_returned_signal"); 
            socket.off("user_left"); socket.off("peer_mute_change"); socket.off("peer_screen_share_change");
            socket.off("peer_view_status_change"); socket.off("receive_message"); socket.off("force_mute"); socket.off("you_are_kicked"); socket.off("receive_private_message");
        };
    }, [addPeer, currentRoom, leaveTable, showChat]);

    
    const joinTable = (room) => {
    // 1. Correctly find the room in the lobby using the room object passed in
    const activeUsers = lobby.find(l => String(l.roomID) === String(room.id))?.users || [];

    // 2. Perform the capacity check
    if (activeUsers.length >= Number(room.max_participants)) {
        return alert("This room is currently full. Please try another one!");
    }

    // 3. Proceed with Media and Socket logic
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        userStream.current = stream;
        setInRoom(true); 
        setCurrentRoom(room.id); // Use room.id instead of id

        socket.emit("join_room", room.id, user);

        socket.on("all_users", users => {
            const newPeers = [];
            users.forEach(u => {
                const peer = createPeer(u.id, socket.id, stream);
                peersRef.current.push({ peerID: u.id, peer });
                newPeers.push({ 
                    peer, 
                    name: u.fullName, 
                    id: u.id, 
                    avatar: u.avatar,
                    db_id: u.db_id,  
                    remoteMuted: u.isMuted, 
                    isSharing: u.isSharing 
                });
            });
            setPeers(newPeers);
        });
    }).catch(err => {
        console.error("Mic access denied:", err);
        alert("Microphone access is required to join the room.");
    });
};

    const toggleScreenShare = async () => {
        if (!isSharing) {
            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({ cursor: true, audio: true });
                screenStream.current = stream;
                const track = stream.getVideoTracks()[0];
                peersRef.current.forEach(({ peer }) => { if(!peer.destroyed) peer.addTrack(track, userStream.current); });
                setIsSharing(true);
                socket.emit("toggle_screen_share", currentRoom, true);
                track.onended = () => { setIsSharing(false); socket.emit("toggle_screen_share", currentRoom, false); };
            } catch (err) { console.error(err); }
        } else {
            if(screenStream.current) screenStream.current.getTracks().forEach(t => t.stop());
            setIsSharing(false);
            socket.emit("toggle_screen_share", currentRoom, false);
        }
    };


    const toggleMute = () => {
        const status = !isMuted;
        if (userStream.current) {
            userStream.current.getAudioTracks()[0].enabled = !status;
            setIsMuted(status);
            socket.emit("toggle_mute", currentRoom, status);
        }
    };

    // --- Action Handlers ---



    const handleFollow = (id) => {
        setFollowing(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        setActiveMenu(null);
    };

    const handleBlock = (id) => {
        setBlocked(prev => [...prev, id]);
        setPeers(prev => prev.filter(p => p.id !== id));
        setActiveMenu(null);
    };

    const handleReport = (id) => {
    // 1. Locally hide the user and prevent future signals
    setBlocked(prev => [...prev, id]);
    setPeers(prev => prev.filter(p => p.id !== id));

    // 2. Notify the server (Optional but recommended for moderation)
    socket.emit("report_user", { 
        targetID: id, 
        reporterID: socket.id, 
        roomID: currentRoom,
        reason: "User reported via UI" 
    });

    // 3. Close the menu
    setActiveMenu(null);
};

const handleTransfer = async (targetUserId) => {
    try {
        const response = await fetch('http://localhost:5000/api/rooms/transfer-owner', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                room_id: currentRoom,
                new_owner_id: targetUserId
            })
        });

        if (response.ok) {
            const updatedRoom = await response.json();
            // Update the activeRoomData state so the UI reflects the new owner 
            setActiveRoomData(updatedRoom);
            
            // Optionally notify others via socket
            socket.emit("owner_changed", { roomID: currentRoom, newOwner: targetUserId });
        }
    } catch (err) {
        console.error("Transfer failed:", err);
    } finally {
        setActiveMenu(null);
    }
};


const updateStatus = (statusValue) => {
    // If clicking what's already selected, clear it. Otherwise, set the new one.
    const nextStatus = myStatus === statusValue ? "" : statusValue;
    
    setMyStatus(nextStatus);
    
    if (currentRoom) {
        socket.emit("update_status", { 
            roomID: currentRoom, 
            status: nextStatus 
        });
    }
};

    const startPrivateChat = (id, name) => {
    setPrivateChatTarget({ id, name });
    setShowChat(true);
    setActiveMenu(null); // Close the overlay menu
    
    // Mark these specific messages as read
    setMessages(prev => prev.map(m => 
        (m.senderID === id && m.isPrivate) ? { ...m, read: true } : m
    ));
};

    const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    // 1. SHOW LOADING MODAL
    setIsLoading(true);

    const path = authMode === "login" ? "/api/login" : "/api/signup";
    
    try {
        const res = await fetch(`http://localhost:5000${path}`, {
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(emailData)
        });

        const data = await res.json();

        if (res.ok) {
            if (authMode === "login") { 
                localStorage.setItem("bt_user_session", JSON.stringify(data.user));
                setUser(data.user); 
                setAuthMode("authenticated"); 
            } else { 
                alert("Signup success! Please check your email for the OTP.");
                setAuthMode("verify"); 
            }
        } else {
            if (data.error === "Account is inactive. Please contact support.") {
                alert("Your account isn't verified yet. Redirecting to verification.");
                setAuthMode("verify");
            } else {
                alert(data.error);
            }
        }
    } catch (err) {
        alert("Something went wrong. Please try again.");
    } finally {
        // 2. HIDE LOADING MODAL (Runs on success OR error)
        setIsLoading(false);
    }
};

   // --- Function to Verify the OTP ---
const handleVerifyOTP = async (e) => {
    e.preventDefault();
    try {
        const res = await fetch(`http://localhost:5000/api/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailData.email, otp: otpCode })
        });
        const data = await res.json();

        if (res.ok) {
            alert("Account activated! You can now login.");
            setAuthMode("login"); 
        } else {
            alert(data.error);
        }
    } catch (err) {
        alert("Verification failed. Please check your connection.");
    }
};

// --- Function to Resend the OTP ---
const handleResendOTP = async () => {
    if (!emailData.email) return alert("Email is required to resend code.");
    
    setIsLoading(true);
    
    // Start a timer to ensure the modal stays visible for at least 600ms
    const minWait = new Promise(resolve => setTimeout(resolve, 600));

    try {
        const endpoint = (authMode === "forgot" || authMode === "reset-form") 
            ? "/api/forgot-password" 
            : "/api/resend-otp";

        const res = await fetch(`http://localhost:5000${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailData.email })
        });

        // Wait for both the network AND the minimum timer
        await minWait;

        if (res.ok) {
            alert("A new 6-digit code has been sent to your email.");
        } else {
            const data = await res.json();
            alert(data.error || "Failed to resend code.");
        }
    } catch (err) {
        await minWait;
        alert("Server connection failed.");
    } finally {
        setIsLoading(false);
    }
};


 // STEP 1: Request code
const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const res = await fetch(`http://localhost:5000/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailData.email })
    });
    setIsLoading(false);
    if (res.ok) {
        setAuthMode("reset-form"); // Move to verification screen
    } else {
        const data = await res.json();
        alert(data.error);
    }
};

// STEP 2: Verify the code first
const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // We call a temporary check or reuse verify-otp logic
    const res = await fetch(`http://localhost:5000/api/verify-reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailData.email, otp: otpCode })
    });
    setIsLoading(false);
    if (res.ok) {
        setIsCodeVerified(true); // This unlocks the password fields
    } else {
        const data = await res.json();
        alert(data.error);
    }
};

// STEP 3: Final Password Update
const handleResetPassword = async (e) => {
    e.preventDefault();
    if (emailData.password !== confirmPassword) return alert("Passwords do not match");

    setIsLoading(true);
    const res = await fetch(`http://localhost:5000/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            email: emailData.email, 
            otp: otpCode, 
            newPassword: emailData.password 
        })
    });
    setIsLoading(false);
    if (res.ok) {
        alert("Password updated!");
        setAuthMode("login");
        setIsCodeVerified(false);
    } else {
        const data = await res.json();
        alert(data.error);
    }
};

const navigateWithLoading = (mode) => {
    setIsLoading(true);
    // Artificial delay so the user actually sees the transition/loading modal
    setTimeout(() => {
        setAuthMode(mode);
        setIsCodeVerified(false); // Reset verification state if moving
        setIsLoading(false);
    }, 200); // 600ms is usually the "sweet spot" for felt responsiveness
};


const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        setPreviewImage(URL.createObjectURL(file)); // For instant preview
        setSelectedFile(file); // For backend upload
    }
};

const handleUpdateAvatar = async () => {
    if (!selectedFile) return setShowProfileModal(false);
    
    setIsLoading(true);
    const formData = new FormData();
    formData.append('userId', user.id);
    formData.append('avatar', selectedFile);

    try {
        const res = await fetch('http://localhost:5000/api/update-avatar', {
            method: 'POST',
            body: formData,
        });

        const data = await res.json();
        if (res.ok) {
            const updatedUser = { ...user, avatar_url: data.avatar_url };
            setUser(updatedUser);
            localStorage.setItem("bt_user_session", JSON.stringify(updatedUser));
            setShowProfileModal(false);
            setPreviewImage(null);
        } else {
            alert(data.error);
        }
    } catch (err) {
        alert("Upload failed.");
    } finally {
        setIsLoading(false);
    }
};



const [activeRoomData, setActiveRoomData] = useState(null);
useEffect(() => {
    const fetchRoomInfo = async () => {
        if (!currentRoom) return; // Don't fetch if not in a room
        
        try {
            // Replace with your actual API endpoint that returns the room row
            const response = await fetch(`http://localhost:5000/api/rooms/details/${currentRoom}`);
            const data = await response.json();
            setActiveRoomData(data); 
        } catch (err) {
            console.error("Error loading room ownership:", err);
        }
    };

    fetchRoomInfo();
}, [currentRoom]);

const [isEditing, setIsEditing] = useState(false);
const [editRoomId, setEditRoomId] = useState(null);

// Function to open modal for Editing
// Open modal for editing
const handleEditClick = (room) => {
    setRoomFormData({
        room_name: room.room_name,
        topic: room.topic,
        language: room.language,
        max_participants: room.max_participants
    });
    setEditRoomId(room.id);
    setIsEditing(true);
    setShowModal(true);
};


// Update handleCreateRoom to handle both POST and PUT
const handleSaveRoom = async (e) => {
    e.preventDefault();
    const url = isEditing 
        ? `http://localhost:5000/api/rooms/${editRoomId}` 
        : 'http://localhost:5000/api/rooms';
    const method = isEditing ? 'PUT' : 'POST';

    try {
        const res = await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...roomFormData, created_by: user.id })
        });

        if (res.ok) {
            const savedRoom = await res.json();
            if (isEditing) {
                setRooms(prev => prev.map(r => r.id === editRoomId ? savedRoom : r));
                setActiveRoomData(savedRoom); // Update current room state
            } else {
                setRooms(prev => [...prev, savedRoom]);
                joinTable(savedRoom.id);
            }
            setShowModal(false);
            setIsEditing(false);
        }
    } catch (err) { console.error(err); }
};






    // 1. Define the list of options
const statuses = [
    "Away 🌙", 
    "Be right back 🕒", 
    "Busy ⛔", 
    "Sleepy 😴", 
    "Bored 😑",
    "Angry 😡",
    "Feeling happy 😊",
    "Working from home 🏠",
    "Outdoor 🏢",
    "At the gym 🏋️",
    "On vacation ✈️",
    "Deep work 🧠",
    "Outdoor 🌳",
    "Enjoying the sun ☀️",
    "Enjoying the rain 🌧️",
    "Chilly weather ❄️", 
    "Driving 🚗", 
    "Don't disturb 🤫", 
    "Out for a coffee ☕", 
    "In a Meeting 💼", 
    "Lunch Break 🍕",
    "In a call 📞",
    "✋ I have a question"
];

// Inside your App function:
const [myStatus, setMyStatus] = useState("");


if (authMode !== "authenticated") {
    return (
        <div style={styles.authContainer}>
            {/* 1. LOADING MODAL (Global) */}
            {isLoading && (
                <div className="loadingOverlay">
                    <div className="spinner"></div>
                    <p style={{ color: '#fff', marginTop: '15px', fontFamily: 'sans-serif' }}>Processing...</p>
                </div>
            )}

            <div style={styles.authCard}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ background: '#6c2dff', padding: '10px', borderRadius: '15px', display: 'inline-block' }}>
                        <MessageSquare color="#0B141A" fill="#0B141A" />
                    </div>
                    <h2 style={{ margin: 0, color: "#ffffff" }}>eLearning</h2>
                    <h2 style={{ marginTop: '10px', color: '#fcfbff' }}>
                        {authMode === "login" ? "Login" : 
                         authMode === "signup" ? "Sign Up" : 
                         authMode === "verify" ? "Verify Account" : 
                         authMode === "forgot" ? "Forgot Password" : "Reset Password"}
                    </h2>
                </div>

                {/* 2. FORGOT PASSWORD SCREEN */}
                {authMode === "forgot" ? (
                    <form onSubmit={handleForgotPassword}>
                        <p style={{ color: '#8696A0', textAlign: 'center', fontSize: '14px' }}>
                            Enter your email to receive a reset code.
                        </p>
                        <input 
                            placeholder="Email" type="email" style={styles.input} 
                            onChange={e => setEmailData({...emailData, email: e.target.value})} required 
                        />
                        <button type="submit" style={styles.primaryBtn}>SEND RESET CODE</button>
                        <p onClick={() => navigateWithLoading("login")} style={styles.toggleText}>Back to Login</p>
                    </form>
                ) : 

                /* 3. MULTI-STEP RESET FORM */
                authMode === "reset-form" ? (
                    <div style={{ width: '100%' }}>
                        {!isCodeVerified ? (
                            <form onSubmit={handleVerifyResetCode}>
                                <p style={{ color: '#8696A0', textAlign: 'center', fontSize: '14px' }}>Paste the code from your email.</p>
                                <input 
                                    placeholder="6-Digit Reset Code" style={styles.input} maxLength="6"
                                    onChange={e => setOtpCode(e.target.value)} required 
                                />
                                <button type="submit" style={styles.primaryBtn}>VERIFY CODE</button>
                                
                                <button 
                                    type="button" 
                                    onClick={handleResendOTP} 
                                    style={{ ...styles.toggleText, background: 'none', border: 'none', width: '100%', marginTop: '10px' }}
                                >
                                    Didn't get a code? Resend
                                </button>

                                <p onClick={() => navigateWithLoading("login")} style={styles.toggleText}>Cancel</p>
                            </form>
                        ) : (
                            <form onSubmit={handleResetPassword}>
                                <p style={{ color: '#4CAF50', textAlign: 'center', fontSize: '14px' }}>Code Verified! Set new password.</p>
                                <input 
                                    placeholder="New Password" type="password" style={styles.input} 
                                    onChange={e => setEmailData({...emailData, password: e.target.value})} required 
                                />
                                <input 
                                    placeholder="Confirm Password" type="password" 
                                    style={{...styles.input, border: (confirmPassword && emailData.password !== confirmPassword) ? '1px solid #ff4d4d' : '1px solid #2f3b43'}} 
                                    onChange={e => setConfirmPassword(e.target.value)} required 
                                />
                                <button type="submit" style={styles.primaryBtn}>UPDATE PASSWORD</button>
                            </form>
                        )}
                    </div>
                ) : 

                /* 4. SIGNUP VERIFICATION */
                authMode === "verify" ? (
                    <form onSubmit={handleVerifyOTP}>
                        <p style={{ color: '#8696A0', textAlign: 'center', fontSize: '14px' }}>Enter the 6-digit code sent to your email.</p>
                        <input placeholder="6-Digit Code" style={styles.input} maxLength="6" onChange={e => setOtpCode(e.target.value)} required />
                        <button type="submit" style={styles.primaryBtn}>VERIFY & ACTIVATE</button>
                        
                        <button 
                            type="button" 
                            onClick={handleResendOTP} 
                            style={{ ...styles.toggleText, background: 'none', border: 'none', width: '100%', marginTop: '10px' }}
                        >
                            Resend Activation Code
                        </button>
                    </form>
                ) : (
                    /* 5. LOGIN & SIGNUP */
                    <>
                        <form onSubmit={handleEmailAuth}>
                            {authMode === "signup" && (
                                <input placeholder="Full Name" style={styles.input} onChange={e => setEmailData({...emailData, fullName: e.target.value})} required />
                            )}
                            <input placeholder="Email" type="email" style={styles.input} onChange={e => setEmailData({...emailData, email: e.target.value})} required />
                            <input placeholder="Password" type="password" style={styles.input} onChange={e => setEmailData({...emailData, password: e.target.value})} required />
                            <button type="submit" style={styles.primaryBtn}>{authMode === "login" ? "LOGIN" : "SIGN UP"}</button>
                        </form>
                        {authMode === "login" && (
                            <p onClick={() => navigateWithLoading("forgot")} style={{ ...styles.toggleText, marginTop: '10px', color: '#6c2dff' }}>Forgot Password?</p>
                        )}
                    </>
                )}

                {/* FOOTER TOGGLE */}
                {(authMode === "login" || authMode === "signup") && (
                    <p 
                        onClick={() => navigateWithLoading(authMode === "login" ? "signup" : "login")} 
                        style={styles.toggleText}
                    >
                        {authMode === "login" ? "Need an account? Sign up" : "Already have an account? Login"}
                    </p>
                )}
            </div>
        </div>
    );
}


    // Calculate unread specifically for the user selected in the menu
const unreadFromActivePeer = messages.filter(m => 
    m.senderID === activeMenu && // Message is from this peer
    m.isPrivate &&               // It is a private message
    !m.read                      // You haven't read it yet
).length;

    return (
        <div style={{ background: '#0B141A', color: '#E9EDEF', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' }}>
            {/* Action Menu Overlay */}
            {activeMenu && (
                
                <div style={styles.menuOverlay} onClick={() => setActiveMenu(null)}>
                    
                    <div style={styles.menuCard} onClick={e => e.stopPropagation()}>
                
                {/* Private Message Item with Notification */}
                <div 
                    style={styles.menuItem} 
                    onClick={() => startPrivateChat(activeMenu, peers.find(p => p.id === activeMenu)?.name)}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <Send size={16} color="#00f2ff" /> 
                        Private Message
                    </div>
                    {unreadFromActivePeer > 0 && (
                        <span style={{
                            background: '#00f2ff',
                            color: '#000',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            boxShadow: '0 0 10px rgba(0, 242, 255, 0.5)',
                            animation: 'pulse 1.5s infinite'
                        }}>
                            {unreadFromActivePeer} NEW
                        </span>
                    )}
                </div>
                        <div style={styles.menuItem} onClick={() => handleFollow(activeMenu)}>
                            {following.includes(activeMenu) ? <><UserMinus size={16} /> Unfollow</> : <><UserPlus size={16} color="#44ff00"  /> Follow</>}
                        </div>
 {/* Admin Only: mute others */}
{activeRoomData && user && Number(activeRoomData.created_by) === Number(user.id) && (                       
                        <div 
                         style={{ ...styles.menuItem, color: '#ffffff' }} 
                         onClick={() => {
                         socket.emit("mute_user", { roomID: currentRoom, targetID: activeMenu });
                         setActiveMenu(null);
                         }}
                         >
                         <MicOff size={16} color="#ff0000" /> 
                          Mute this user 
                         </div>
                        
)}
 {/* Admin Only: Kick */}
{activeRoomData && user && Number(activeRoomData.created_by) === Number(user.id) && (  
    <div 
        style={{ ...styles.menuItem, borderTop: '1px solid #3B4A54', color: '#11ff00'}} 
        onClick={() => { 
            if (window.confirm(`Kick this user?`)) {
                socket.emit("kick_user", { 
                    roomID: currentRoom, 
                    targetID: activeMenu, 
                    adminID: user.id 
                });
                setActiveMenu(null); 
            }
        }}
    >
        <SportShoe size={16} /> Kick Out
    </div>
)}

                        <div style={{ ...styles.menuItem, borderTop: '1px solid #3B4A54', color: '#ffe600' }} onClick={() => { if (window.confirm(`Block this user ?`)) handleBlock(activeMenu); setActiveMenu(null);}}>
                            <Ban size={16} /> Block User
                        </div>
                        <div style={{ ...styles.menuItem, color: '#ff4b4b'}} onClick={() => { if (window.confirm(`Report this user?`)) handleReport(activeMenu); setActiveMenu(null);}}>
                            <ShieldBan size={16} /> Report this user
                        </div>
                    </div>
                </div>
            )}

            <header style={styles.header}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ background: '#6c2dff', padding: '8px', borderRadius: '12px' }}>
            <MessageSquare color="#0B141A" fill="#0B141A" />
        </div>
        <h2 style={{ margin: 0 }}>eLearning</h2>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        {/* CLICKABLE AVATAR AREA */}
        <div 
            onClick={() => setShowProfileModal(true)} 
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
        >
            <img 
                src={user?.avatar_url || `https://ui-avatars.com/api/?name=${user?.fullName}`} 
                style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #6c2dff' }} 
                alt="p" 
            />
            <span>{user?.fullName}</span>
        </div>

        <button onClick={() => { localStorage.removeItem("bt_user_session"); window.location.reload(); }} style={styles.logoutBtn}>
            <LogOut size={18} />
        </button>
    </div>
</header>
{showProfileModal && (
    <div className="loadingOverlay" style={{ zIndex: 10000 }}>
        <div style={{ 
            ...styles.authCard, 
            width: '340px', 
            textAlign: 'center', 
            padding: '30px',
            border: '1px solid rgba(108, 45, 255, 0.2)' // Subtle purple border
        }}>
            <h3 style={{ color: '#fff', margin: '0 0 10px 0' }}>Change Profile Photo</h3>
            
            <div style={{ marginBottom: '5px' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    {/* Main Avatar Display */}
                    <img 
                        src={previewImage || user?.avatar_url || `https://ui-avatars.com/api/?name=${user?.fullName}&background=6c2dff&color=fff`} 
                        style={{ 
                            width: '130px', 
                            height: '130px', 
                            borderRadius: '50%', 
                            objectFit: 'cover', 
                            border: '4px solid #6c2dff',
                            boxShadow: '0 0 20px rgba(108, 45, 255, 0.3)', // Soft glow
                            padding: '4px',
                            background: '#0B141A'
                        }} 
                        alt="Profile" 
                    />
                    
                    {/* The "Rechargeable" Action Circle */}
                    <label 
                        htmlFor="avatar-upload" 
                        className="uploadCircle" // Using the CSS class we defined
                        style={{
                            ...styles.uploadCircle,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {/* Switched to a more logical Camera-style icon */}
                        <div style={{ transform: 'translateY(-1px)' }}>
                            <Camera size={16} color="#fff" />
                        </div>
                    </label>
                    
                    <input 
                        id="avatar-upload" 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        style={{ display: 'none' }} 
                    />
                </div>
            </div>
                    <h3 style={{ color: '#fff', margin: '0 0 15px 0' }}>{user?.fullName}</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px'}}>
                {/* Dynamic Action Button */}
                <button 
                    onClick={handleUpdateAvatar} 
                    style={{ 
                        ...styles.primaryBtn, 
                        background: selectedFile ? '#6c2dff' : 'rgba(108, 45, 255, 0.1)',
                        color: selectedFile ? '#fff' : '#6c2dff',
                        border: selectedFile ? 'none' : '1px solid #6c2dff',
                        cursor: (selectedFile && !isLoading) ? 'pointer' : 'not-allowed',
                        opacity: isLoading ? 0.7 : 1
                    }}
                    disabled={!selectedFile || isLoading}
                >
                    {isLoading ? "UPLOADING..." : selectedFile ? "SAVE NEW PHOTO" : "SELECT A PHOTO"}
                </button>
                
                {/* Secondary Cancel Action */}
                <button 
                    onClick={() => { 
                        setShowProfileModal(false); 
                        setPreviewImage(null); 
                        setSelectedFile(null); 
                    }} 
                    style={{ 
                        margin: ' 10px 0 0px 0',
                        background: 'transparent', 
                        border: 'none', 
                        color: '#ffffff', 
                        cursor: 'pointer', 
                        fontSize: '16px',
                        padding: '10px',
                        textDecoration: 'underline'
                    }}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}

            <main style={{ padding: '40px' }}>
                            {!inRoom ? (
                                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Globe size={22} color="#6c2dff" /><h3 style={{ margin: 0 }}>Lobby</h3></div><button onClick={() => setShowModal(true)} style={{ ...styles.joinBtn, width: 'auto', padding: '10px 25px', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}><PlusCircle size={18} /> CREATE A ROOM</button></div>
                                    {showModal && (
                                        <div style={styles.modalOverlay}><div style={styles.modalCard}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}><h3 style={{ margin: 0 }}>Create a new room</h3><X style={{ cursor: 'pointer' }} onClick={() => setShowModal(false)} /></div><form onSubmit={handleSaveRoom}><input placeholder="Room Name" style={styles.input} required onChange={e => setRoomFormData({...roomFormData, room_name: e.target.value})} /><textarea placeholder="Subject" style={styles.input} required onChange={e => setRoomFormData({...roomFormData, topic: e.target.value})} /><select style={{ ...styles.input, appearance: 'none' }}  required onChange={e => setRoomFormData({...roomFormData, language: e.target.value})}><option >Choose Language</option>
  <option value="Afrikaans">Afrikaans</option>
  <option value="Albanian">Albanian</option>
  <option value="Amharic">Amharic</option>
  <option value="Arabic">Arabic</option>
  <option value="Armenian">Armenian</option>
  <option value="Azerbaijani">Azerbaijani</option>
  <option value="Bengali">Bengali</option>
  <option value="Bosnian">Bosnian</option>
  <option value="Bulgarian">Bulgarian</option>
  <option value="Burmese">Burmese</option>
  <option value="Catalan">Catalan</option>
  <option value="Chinese (Simplified)">Chinese (Simplified)</option>
  <option value="Chinese (Traditional)">Chinese (Traditional)</option>
  <option value="Croatian">Croatian</option>
  <option value="Czech">Czech</option>
  <option value="Danish">Danish</option>
  <option value="Dutch">Dutch</option>
  <option value="English">English</option>
  <option value="Estonian">Estonian</option>
  <option value="Farsi">Farsi</option>
  <option value="Filipino">Filipino</option>
  <option value="Finnish">Finnish</option>
  <option value="French">French</option>
  <option value="Georgian">Georgian</option>
  <option value="German">German</option>
  <option value="Greek">Greek</option>
  <option value="Gujarati">Gujarati</option>
  <option value="Hebrew">Hebrew</option>
  <option value="Hindi">Hindi</option>
  <option value="Hungarian">Hungarian</option>
  <option value="Icelandic">Icelandic</option>
  <option value="Indonesian">Indonesian</option>
  <option value="Italian">Italian</option>
  <option value="Japanese">Japanese</option>
  <option value="Kannada">Kannada</option>
  <option value="Kazakh">Kazakh</option>
  <option value="Khmer">Khmer</option>
  <option value="Korean">Korean</option>
  <option value="Latvian">Latvian</option>
  <option value="Lithuanian">Lithuanian</option>
  <option value="Macedonian">Macedonian</option>
  <option value="Malay">Malay</option>
  <option value="Malayalam">Malayalam</option>
  <option value="Marathi">Marathi</option>
  <option value="Mongolian">Mongolian</option>
  <option value="Nepali">Nepali</option>
  <option value="Norwegian">Norwegian</option>
  <option value="Pashto">Pashto</option>
  <option value="Polish">Polish</option>
  <option value="Portuguese">Portuguese</option>
  <option value="Punjabi">Punjabi</option>
  <option value="Romanian">Romanian</option>
  <option value="Russian">Russian</option>
  <option value="Serbian">Serbian</option>
  <option value="Sinhala">Sinhala</option>
  <option value="Slovak">Slovak</option>
  <option value="Slovenian">Slovenian</option>
  <option value="Spanish">Spanish</option>
  <option value="Swahili">Swahili</option>
  <option value="Swedish">Swedish</option>
  <option value="Tamil">Tamil</option>
  <option value="Telugu">Telugu</option>
  <option value="Thai">Thai</option>
  <option value="Turkish">Turkish</option>
  <option value="Ukrainian">Ukrainian</option>
  <option value="Urdu">Urdu</option>
  <option value="Uzbek">Uzbek</option>
  <option value="Vietnamese">Vietnamese</option>
  <option value="Zulu">Zulu</option>
</select>
<select style={{ ...styles.input, appearance: 'none' }} onChange={e => setRoomFormData({...roomFormData, max_participants: e.target.value})}  required>
  <option >Choose no. of participants</option>
  <option value="1">1</option>
  <option value="2">2</option>
  <option value="3">3</option>
  <option value="4">4</option>
  <option value="5">5</option>
  <option value="6">6</option>
  <option value="7">7</option>
  <option value="8">8</option>
  <option value="9">9</option>
  <option value="10">10</option>
  <option value="11">11</option>
  <option value="12">12</option>
  <option value="13">13</option>
  <option value="14">14</option>
  <option value="15">15</option>
  <option value="16">16</option>
  <option value="17">17</option>
  <option value="18">18</option>
  <option value="19">19</option>
  <option value="20">20</option>
  <option value="21">21</option>
  <option value="22">22</option>
  <option value="23">23</option>
  <option value="24">24</option>
  <option value="25">25</option>
  <option value="26">26</option>
  <option value="27">27</option>
  <option value="28">28</option>
  <option value="29">29</option>
  <option value="30">30</option>
  <option value="31">31</option>
  <option value="32">32</option>
  <option value="33">33</option>
  <option value="34">34</option>
  <option value="35">35</option>
  <option value="36">36</option>
  <option value="37">37</option>
  <option value="38">38</option>
  <option value="39">39</option>
  <option value="40">40</option>
  <option value="41">41</option>
  <option value="42">42</option>
  <option value="43">43</option>
  <option value="44">44</option>
  <option value="45">45</option>
  <option value="46">46</option>
  <option value="47">47</option>
  <option value="48">48</option>
  <option value="49">49</option>
  <option value="50">50</option>
</select>
<button type="submit" style={styles.primaryBtn}>Create & Join</button></form></div></div>
                                    )}
                                    
                                    {/* USER DETAILS MODAL */}
                                    {selectedUser && (
                                        <div style={styles.modalOverlay} onClick={() => setSelectedUser(null)}>
                                            <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                                    <h3 style={{ margin: 0 }}>User Profile</h3>
                                                    <X style={{ cursor: 'pointer', color: '#8696A0' }} onClick={() => setSelectedUser(null)} />
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                                    <div style={{
                                                        width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 15px',
                                                        backgroundImage: selectedUser.avatar ? `url(${selectedUser.avatar})` : 'none',
                                                        backgroundColor: '#3B4A54', backgroundSize: 'cover', backgroundPosition: 'center',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px',
                                                        border: '3px solid #6c2dff'
                                                    }}>
                                                        {!selectedUser.avatar && selectedUser.fullName?.[0].toUpperCase()}
                                                    </div>
                                                    <h2 style={{ color: '#E9EDEF', marginBottom: '5px' }}>{selectedUser.name}</h2>
                                                    <p style={{ color: '#8696A0', fontSize: '14px' }}>Active Member</p>
                                                </div>
                                                <button onClick={() => setSelectedUser(null)} style={styles.primaryBtn}>CLOSE</button>
                                            </div>
                                        </div>
                                    )}
            
                                <div style={styles.roomGrid}>
    {rooms.map(room => {
        const activeUsers = lobby.find(l => String(l.roomID) === String(room.id))?.users || [];
        // Define if room is full based on max_participants
        const isFull = activeUsers.length >= Number(room.max_participants);

        return (
            <div key={room.id} style={styles.roomCard}>
                <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', marginLeft: '2.5px', gap: '8px', marginBottom: '8px' }}>
                        <h3 style={{ margin: '0px 0 0 0' }}>{room.room_name}</h3>
                    </div>

                    <span style={styles.sub}>{room.language}</span>
                    <p style={{ fontSize: '12px', margin: '10px 0px 40px 0' }}>Subject: {room.topic}</p>
                    
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex', marginRight: '10px' }}>
                            {activeUsers.slice(0, 5).map((u, i) => (
                                <div
                                    key={i}
                                    title={u.fullName}
                                    onClick={() => setSelectedUser(u)}
                                    style={{
                                        ...styles.miniAvatar,
                                        backgroundImage: u.avatar ? `url(${u.avatar})` : 'none',
                                        marginLeft: i === 0 ? 0 : '1px',
                                        zIndex: 10 - i,
                                        backgroundColor: u.avatar ? 'transparent' : '#3B4A54',
                                        cursor: 'pointer'
                                    }}
                                />
                            ))}
                        </div>
                        {/* Status Counter: Turns red if full */}
                        <span style={{ 
                            color: isFull ? '#ff4d4d' : '#8696A0', 
                            fontSize: '13px', 
                            marginLeft: 'auto',
                            fontWeight: isFull ? 'bold' : 'normal' 
                        }}>
                            <Users size={14} /> {activeUsers.length}/{room.max_participants}
                        </span>
                    </div>
                </div>

                {/* JOIN BUTTON: Disabled and styled if full */}
                <button 
                    onClick={() => !isFull && joinTable(room)} 
                    disabled={isFull}
                    style={{ 
                        ...styles.joinBtn, 
                        opacity: isFull ? 0.6 : 1,
                        cursor: isFull ? 'not-allowed' : 'pointer',
                        filter: isFull ? 'grayscale(0.5)' : 'none'
                    }}
                >
                    <PhoneCall size={20} /> 
                    {isFull ? " ROOM FULL " : " JOIN NOW "} 
                    {!isFull && <SquareArrowRightEnter size={12} />}
                </button>
            </div>
        );
    })}
</div>
                    </div>
                ) : (
                    <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '300px' }}>
                            <div style={styles.roomStage}>
                                <Avatar
                                name={user?.fullName}
                                avatarUrl={user?.avatar_url}
                                // If sharing, use the screenStream ref; otherwise, use the userStream ref
                                 stream={isSharing ? screenStream.current : userStream.current}
                                 isMuted={isMuted}
                                 isSharing={isSharing}
                                 isViewing={isSharing} // Force viewing mode for self-share so the video renders
                                 color="#6c2dff"
                                 isSelf
                                 status={myStatus} // FIX 1: Pass your own status here
                                 viewers={viewingMap[socket.id] || []}
                                 />
                                {peers.map(p => (
                                    <RemoteParticipant 
                                        key={p.id} participantID={p.id} peer={p.peer} name={p.name} 
                                        avatarUrl={p.avatar} remoteMuted={p.remoteMuted} isSharing={p.isSharing}
                                        status={p.status} // FIX 2: Pass the peer's status here
                                        currentRoom={currentRoom} viewers={viewingMap[p.id] || []} 
                                        onMenuOpen={setActiveMenu}
                                    />
                                ))}
                            </div>
                      <div style={styles.controls}>
    {/* ROOM NAME SECTION + EDIT BUTTON */}
    <div style={{ width: '100%', marginBottom: '7px', display: 'flex', gap: '15px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            {rooms.filter(r => String(r.id) === String(currentRoom)).map(r => (
                <React.Fragment key={r.id}>
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#ffffff' }}>Room: </h2>
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#c1a6ff' }}>{r.room_name}</h2>
                    
                    {/* EDIT BUTTON: Only shows if you are the creator */}
                    {Number(r.created_by) === Number(user?.id) && (
                        <button 
                            onClick={() => handleEditClick(r)} 
                            style={{
                                background: 'transparent',
                                border: '1px solid #6c2dff',
                                color: '#6c2dff',
                                borderRadius: '4px',
                                padding: '2px 8px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                marginLeft: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <Edit size={12} /> Edit
                        </button>
                    )}
                </React.Fragment>
            ))}
        </div>
    </div>
    {showModal && (
    <div className="modal-overlay">
        <div className="modal-content">
            <h3>{isEditing ? 'Edit Room' : 'Create Room'}</h3>
            
            <form onSubmit={handleSaveRoom}>
                {/* Room Name */}
                <div className="input-group">
                    <label className="label">Room Name</label>
                    <input 
                        className="input"
                        value={roomFormData.room_name} 
                        onChange={(e) => setRoomFormData({...roomFormData, room_name: e.target.value})} 
                        required 
                    />
                </div>

                {/* Topic */}
                <div className="input-group">
                    <label className="label">Topic</label>
                    <input 
                        className="input"
                        value={roomFormData.topic} 
                        onChange={(e) => setRoomFormData({...roomFormData, topic: e.target.value})} 
                    />
                </div>

                {/* Language Selection */}
                <div className="input-group">
                    <label className="label">Language</label>
                    <select 
                        className="input"
                        value={roomFormData.language} 
                        onChange={(e) => setRoomFormData({...roomFormData, language: e.target.value})}
                    >
                        <option value="English">English</option>
                        <option value="Spanish">Spanish</option>
                        <option value="French">French</option>
                    </select>
                </div>

                {/* Max Participants */}
                <div className="input-group">
                    <label className="label">Max Participants</label>
                    <input 
                        type="number" 
                        className="input"
                        value={roomFormData.max_participants} 
                        onChange={(e) => setRoomFormData({...roomFormData, max_participants: e.target.value})} 
                    />
                </div>

                {/* Transfer Room Ownership - Only show when editing if user is owner */}
{isEditing && activeRoomData?.created_by === user?.id && peers.length > 0 && (
    <div className="input-group" style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
        <label className="label" style={{ color: '#ff4d4d' }}>Transfer Ownership</label>
        <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
            
            {/* Custom Dropdown Container */}
            <div style={{ flex: 1, position: 'relative' }}>
                <div 
                    className="input" 
                    onClick={() => setIsTransferOpen(!isTransferOpen)}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        minHeight: '40px'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {selectedUser ? (
                            <>
                                <img 
                                    src={peers.find(p => p.db_id === selectedUser)?.avatar || '/default-avatar.png'} 
                                    style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover' }} 
                                    alt="" 
                                />
                                <span>{peers.find(p => p.db_id === selectedUser)?.name}</span>
                            </>
                        ) : (
                            <span style={{ color: '#8696a0' }}>Select a user...</span>
                        )}
                    </div>
                    <span style={{ fontSize: '10px', opacity: 0.6 }}>{isTransferOpen ? '▲' : '▼'}</span>
                </div>

                {/* Dropdown List */}
                {isTransferOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '110%',
                        left: 0,
                        right: 0,
                        background: '#233138',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        maxHeight: '180px',
                        overflowY: 'auto',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        {peers.map(p => (
                            <div 
                                key={p.id}
                                onClick={() => {
                                    setSelectedUser(p.db_id);
                                    setIsTransferOpen(false);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '10px',
                                    cursor: 'pointer',
                                    background: selectedUser === p.db_id ? 'rgba(147, 0, 0, 0.2)' : 'transparent',
                                    transition: '0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = selectedUser === p.db_id ? 'rgba(147, 0, 0, 0.2)' : 'transparent'}
                            >
                                <img 
                                    src={p.avatar || '/default-avatar.png'} 
                                    style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} 
                                    alt="" 
                                />
                                <span style={{ color: '#fff', fontSize: '14px' }}>{p.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button 
                type="button" 
                className="submit-btn" 
                style={{ background: '#da2a0b', fontSize: '12px', whiteSpace: 'nowrap', height: '42px' }}
                onClick={() => {
                    if (selectedUser) {
                        handleTransfer(selectedUser); // Execute the transfer logic
                        setShowModal(false);         // Close the modal
                        setIsEditing(false);        // Reset editing state
                    }
                }}
            >
                Transfer <ArrowBigRightDash size={12} />
            </button>
        </div>
    </div>
)}

                {/* Buttons Container */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            setShowModal(false);
                            setIsEditing(false);
                        }} 
                        className="cancel-btn"
                    >
                        Cancel
                    </button>
                    <button type="submit" className="submit-btn">
                        {isEditing ? 'Save Changes' : 'Create Room'}
                    </button>
                </div>
            </form>
        </div>
    </div>
)}
    {/* LANGUAGE SECTION */}
    <div style={{ width: '100%', marginBottom: '7px', display: 'flex', gap: '15px' }}>
        {rooms.filter(r => String(r.id) === String(currentRoom)).map(r => (
            <React.Fragment key={r.id}>
                <h4 style={{ margin: 0, fontSize: '16px' }}>Language: </h4>
                <span style={{ ...styles.sub, backgroundColor: '#6c2dff', color: '#ffffff', padding: '2px 8px', borderRadius: '4px' }}>
                    {r.language}
                </span>
            </React.Fragment>
        ))}
    </div>
     

    {/* TOPIC SECTION */}
    <div style={{ width: '100%', marginBottom: '7px', display: 'flex', gap: '15px' }}>
        {rooms.filter(r => String(r.id) === String(currentRoom)).map(r => (
            <React.Fragment key={r.id}>
                <h4 style={{ margin: 0, fontSize: '14px' }}>Topic: </h4>
                <span style={{ fontSize: '14px', color: '#8696A0' }}>{r.topic}</span>
            </React.Fragment>
        ))}
    </div>
{/* MAX PARTICIPANTS SECTION */}
<div style={{ width: '100%', marginBottom: '7px', display: 'flex', gap: '15px' }}>
    {rooms.filter(r => String(r.id) === String(currentRoom)).map(r => (
        <React.Fragment key={r.id}>
            <h4 style={{ margin: 0, fontSize: '14px' }}>Capacity: </h4>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '14px', 
                color: '#8696A0' 
            }}>
                <Users size={14} color="#6c2dff" />
                <span>
                    {/* If you have a peers array, you can show peers.length + 1 */}
                    {r.max_participants} Max Users
                </span>
            </div>
        </React.Fragment>
    ))}
  </div> 
</div>                   
                            
<div style={styles.controls}>

                      {/* --- STATUS PICKER (Click to Select) --- */}
<div style={{ 
    display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', 
    padding: '12px', maxWidth: '600px', background: 'rgba(15, 23, 42, 0.4)', 
    borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
}}>
    {/* Clear Button */}
    <button 
        onClick={() => updateStatus("")}
        style={{ 
            padding: '5px 12px', fontSize: '10px', borderRadius: '15px', 
            border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', 
            cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s ease',
            opacity: myStatus ? 1 : 0.5
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
        ✕ Reset
    </button>

    {statuses.map(s => {
        const isSelected = myStatus === s;
        return (
            <button 
                key={s}
                onClick={() => updateStatus(s)}
                style={{
                    padding: '5px 12px', 
                    fontSize: '11px', 
                    borderRadius: '15px', 
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    
                    // Dynamic Styles based on selection
                    border: isSelected ? '1px solid #a855f7' : '1px solid #3B4A54',
                    background: isSelected ? '#6c2dff' : 'rgba(59, 74, 84, 0.3)',
                    color: isSelected ? '#ffffff' : '#94a3b8',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isSelected ? '0 0 15px rgba(108, 45, 255, 0.4)' : 'none',
                }}
                onMouseEnter={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.background = 'rgba(75, 85, 99, 0.5)';
                        e.currentTarget.style.color = '#fff';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.background = 'rgba(59, 74, 84, 0.3)';
                        e.currentTarget.style.color = '#94a3b8';
                    }
                }}
            >
                {s}
            </button>
        );
    })}
</div>         
                                
                <button onClick={toggleMute} style={{ ...styles.circleBtn, background: isMuted ? '#dd2424' : '#3B4A54' }}>
                    {isMuted ? <MicOff /> : <Mic />}
                </button>
                
                <button onClick={toggleScreenShare} style={{ ...styles.circleBtn, background: isSharing ? '#6c2dff' : '#3B4A54' }}>
                    {isSharing ? <MonitorOff /> : <MonitorUp />}
                </button>

                <button 
    onClick={() => { 
        const willOpen = !showChat;
        setShowChat(willOpen); 

        if (willOpen) {
            // Force the chat to open in Group/Public mode
            setPrivateChatTarget(null);
            
            // Clear the unread count for the public room
            setUnreadCount(0);
        }
    }} 
    style={{ ...styles.circleBtn, position: 'relative' }}
>
    <MessageSquare />
    
    {/* Badge: ONLY shows unreadCount for the group room */}
    {unreadCount > 0 && !showChat && (
        <div style={{
            ...styles.badge,
            background: '#6c2dff' // Your standard room chat color
        }}>
            {unreadCount}
        </div>
    )}
</button>
                
                <button 
    onClick={() => { 
        const willOpen = !showChat;
        setShowChat(willOpen); 

        if (willOpen) {
            const allMessages = messages || [];
            // Look only for private messages
            const firstUnreadPrivate = allMessages.find(m => m.isPrivate && !m.read);
            
            if (firstUnreadPrivate) {
                const target = { 
                    id: firstUnreadPrivate.senderID, 
                    name: firstUnreadPrivate.senderName 
                };
                setPrivateChatTarget(target);

                // Mark as read for this specific person
                setMessages(prev => (prev || []).map(m => 
                    (m.isPrivate && m.senderID === target.id) 
                    ? { ...m, read: true } 
                    : m
                ));
            } 
            // Note: If no private messages, it just opens the last set target or empty state
        }
    }} 
    style={{ ...styles.circleBtn, position: 'relative' }}
>
    <Mail />
    
    {/* Badge: ONLY Private Unread Logic */}
    {!showChat && (messages || []).some(m => m.isPrivate && !m.read) && (
        <div style={{
            ...styles.badge,
            background: '#00f2ff', // Strictly using your private color
            boxShadow: '0 0 12px rgba(0, 242, 255, 0.6)'
        }}>
            {(messages || []).filter(m => m.isPrivate && !m.read).length}
        </div>
    )}
</button>
                
                <button onClick={leaveTable} style={{ ...styles.circleBtn, background: '#6c2dff', width: 'auto', padding: '0 25px', borderRadius: '35px' }}>
                    <LogOut size={20} /> 
                    <span style={{ marginLeft: '10px' }}>LEAVE ROOM</span>
                </button>
            </div>
          </div>
                        {showChat && (
    <div 
        onClick={() => { setShowChat(false); setPrivateChatTarget(null); }}
        style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center', 
            justifyContent: 'center', 
            backgroundColor: 'rgba(15, 23, 42, 0.75)', 
            backdropFilter: 'blur(8px)',
            padding: '20px'
        }}
    >
        <div 
            onClick={(e) => e.stopPropagation()} 
            style={{
                width: '450px',
                height: '80vh',
                maxWidth: '100%',
                maxHeight: '700px',
                position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
                animation: 'popupAppear 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: '#1e293b' // Ensure background is solid
            }}
        >
            {/* Explicit Close Button for better UX */}
            

            <ChatSidebar 
                messages={messages.filter(m => {
    // SCENARIO 1: You are looking at the Public Room Chat
    if (!privateChatTarget) {
        return !m.receiverID; // Only show messages that have no specific receiver
    }

    // SCENARIO 2: You are looking at a Private Chat with a specific person
    // Show messages ONLY if (I sent it to them) OR (They sent it to me)
    return (
        (m.senderID === socket.id && m.receiverID === privateChatTarget.id) ||
        (m.senderID === privateChatTarget.id && m.receiverID === socket.id)
    );
})}
                currentUserName={user?.fullName} 
                isPrivate={!!privateChatTarget}
                targetName={privateChatTarget?.name}
                onClose={() => { setShowChat(false); setPrivateChatTarget(null); }} 
                                onSendMessage={(txt) => {
                                    const msg = { 
                                        text: txt, 
                                        senderName: user?.fullName, 
                                        senderID: socket.id,
                                        receiverID: privateChatTarget?.id,
                                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                                    };
                                    socket.emit(privateChatTarget ? "send_private_message" : "send_message", currentRoom, msg);
                                    if(privateChatTarget) setMessages(prev => [...prev, msg]);
                                }} 
                            />
            </div>
    </div>
)}

                       
                    </div>
                )}
                
          </main>

  
    <div>


        {/* THE FOOTER */}
        <footer style={styles.premiumFooter}>
    <div style={styles.footerInner}>
        {/* TOP SECTION: GRID */}
        <div style={styles.footerGrid}>
            
            {/* Column 1: Brand & Status */}
            <div style={styles.footerCol}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ background: '#6c2dff', padding: '8px', borderRadius: '12px' }}>
            <MessageSquare color="#0B141A" fill="#0B141A" />
        </div>
        <h2 style={{ margin: 0 }}>eLearning</h2>
    </div>
                <p style={styles.brandDesc}>
                    Connecting the world through real-time audio rooms. 
                    Practice, learn, and grow together.
                </p>
                <div style={styles.statusBadge}>
                    <span style={styles.statusDot}></span>
                    {rooms.length} Rooms Live
                </div>
            </div>

            {/* Column 2: Platform Links */}
            <div style={styles.footerCol}>
                <h4 style={styles.colTitle}>Platform</h4>
                <button type="button" style={styles.premiumLink}>Browse Rooms</button>
                <button type="button" style={styles.premiumLink}>Create Space</button>
                <button type="button" style={styles.premiumLink}>Community</button>
            </div>

            {/* Column 3: Company Links */}
            <div style={styles.footerCol}>
                <h4 style={styles.colTitle}>Company</h4>
                <button type="button" style={styles.premiumLink}>About Us</button>
                <button type="button" style={styles.premiumLink}>Privacy Policy</button>
                <button type="button" style={styles.premiumLink}>Terms of Service</button>
            </div>

            {/* Column 4: Social/Newsletter */}
            <div style={styles.footerCol}>
                <h4 style={styles.colTitle}>Stay Connected</h4>
                <div style={{ display: 'flex', gap: '12px', marginTop: '5px' }}>
                   {/* Replace with your icon components */}
                   <button type="button" style={styles.socialIcon}>𝕏</button>
                   <button type="button" style={styles.socialIcon}>in</button>
                   <button type="button" style={styles.socialIcon}>ig</button>
                </div>
            </div>
        </div>

        {/* BOTTOM SECTION: COPYRIGHT */}
        <div style={styles.footerBottom}>
            <p style={styles.copyrightText}>
                &copy; {new Date().getFullYear()} eLearning. Developed by BAYTASOFT LIMITED.
            </p>
            <div style={{ display: 'flex', gap: '15px' }}>
                <span style={styles.footerVersion}>v2.4.0</span>
            </div>
        </div>
    </div>
</footer>
    </div>
    
        </div>
    );
};

const styles = {
  // --- Authentication & Layout ---
  authContainer: { height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0F172A', padding: '20px', boxSizing: 'border-box' },
  authCard: { backgroundColor: '#1E293B', padding: '40px', borderRadius: '20px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', border: '1px solid #334155', boxSizing: 'border-box' },
  header: { backgroundColor: '#1E293B', padding: '16px 5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', position: 'sticky', top: 0, zIndex: 100 },
  
  // --- Form Elements & Buttons ---
  input: { width: '100%', padding: '14px 16px', marginBottom: '16px', borderRadius: '10px', border: '1px solid #334155', backgroundColor: '#0F172A', color: '#F1F5F9', fontSize: '15px', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' },
  primaryBtn: { width: '100%', padding: '14px', borderRadius: '10px', border: 'none', backgroundColor: '#6c2dff', color: '#FFFFFF', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' },
  toggleText: { textAlign: 'center', marginTop: '24px', color: '#A78BFA', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s' },
  logoutBtn: { backgroundColor: 'rgba(239, 68, 68, 0.1)', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '10px', borderRadius: '50%', display: 'flex', transition: 'background-color 0.2s' },
  joinBtn: { width: '100%', padding: '12px', backgroundColor: '#6c2dff', border: 'none', color: '#FFFFFF', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px', borderRadius: '10px', transition: 'opacity 0.2s' },
  circleBtn: { width: '52px', height: '52px', borderRadius: '50%', border: 'none', backgroundColor: '#334155', color: '#F1F5F9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
  actionBtn: { backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#F1F5F9', transition: 'background-color 0.2s' },
  sendBtn: { backgroundColor: 'transparent', border: 'none', color: '#6c2dff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', transition: 'transform 0.1s' },
  
  // --- Rooms & Lobby ---
  roomGrid: { display: 'grid', alignContent: 'space-evenly !important', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px', padding: '20px 0' },
  roomCard: { backgroundColor: '#1E293B', borderRadius: '20px', overflow: 'hidden', border: '1px solid #334155', transition: 'transform 0.2s, box-shadow 0.2s', display: 'flex', flexDirection: 'column', cursor: 'pointer' },
  roomStage: { flex: 1, backgroundColor: '#1E293B', borderRadius: '24px', padding: '24px', display: 'grid', alignContent: 'space-evenly', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', minHeight: '65vh', border: '1px solid #334155', boxSizing: 'border-box' },
  controls: { display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '24px', padding: '16px', backgroundColor: '#0F172A', borderRadius: '20px', border: '1px solid #334155' },
  
  // --- Participants & Video ---
  participantSlot: { position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' },
  actionBar: { position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 10 },
  miniAvatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#334155', border: '2px solid #1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600', color: '#F1F5F9', transition: 'transform 0.2s' },
  videoIconBtn: { backgroundColor: 'rgba(15, 23, 42, 0.7)', border: 'none', color: '#F1F5F9', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.2s', backdropFilter: 'blur(4px)' },
  viewerBadge: { position: 'absolute', top: 12, left: 12, backgroundColor: 'rgba(15, 23, 42, 0.8)', padding: '4px 10px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', zIndex: 12, border: '1px solid #334155', backdropFilter: 'blur(4px)' },
  pulseDot: { width: '8px', height: '8px', backgroundColor: '#6c2dff', borderRadius: '50%', boxShadow: '0 0 10px #6c2dff' },
  viewOverlay: { position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 11, borderRadius: 'inherit', backdropFilter: 'blur(2px)' },
  playCircle: { width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#6c2dff', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '4px', boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)', transition: 'transform 0.2s' },
  
  // --- Chat Interface ---
  chatSidebar: { width: '100%', maxWidth: '380px', backgroundColor: '#0F172A', borderRadius: '20px', display: 'flex', flexDirection: 'column', border: '1px solid #334155', height: '75vh', overflow: 'hidden' },
  chatHeader: { padding: '16px 20px', backgroundColor: '#1E293B', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#F1F5F9' },
  messageList: { flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#0F172A' },
  msgBubble: { padding: '10px 14px', borderRadius: '14px', maxWidth: '85%', position: 'relative', fontSize: '14px', lineHeight: '1.4' },
  msgSender: { fontSize: '12px', fontWeight: '600', color: '#A78BFA', marginBottom: '6px' },
  msgTime: { fontSize: '10px', color: 'rgba(241, 245, 249, 0.5)', textAlign: 'right', marginTop: '6px', float: 'right', marginLeft: '10px' },
  chatInputArea: { padding: '16px', backgroundColor: '#1E293B', borderTop: '1px solid #334155', display: 'flex', gap: '12px', alignItems: 'center' },
  chatInput: { flex: 1, backgroundColor: '#334155', border: 'none', borderRadius: '24px', padding: '12px 20px', color: '#F1F5F9', outline: 'none', fontSize: '15px' },
  
  // --- Modals & Menus ---
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', boxSizing: 'border-box', backdropFilter: 'blur(8px)' },
  modalCard: { backgroundColor: '#1E293B', padding: '32px', borderRadius: '24px', width: '100%', maxWidth: '440px', border: '1px solid #334155', boxSizing: 'border-box', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' },
  menuOverlay: { position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'transparent' },
  menuCard: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#1E293B', borderRadius: '16px', width: '240px', boxShadow: '0 20px 40px rgba(0,0,0,0.7)', border: '1px solid #334155', overflow: 'hidden' },
  menuItem: { padding: '14px 20px', color: '#F1F5F9', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'background-color 0.2s', '&:hover': { backgroundColor: '#334155' } },
  
  // --- Indicators & Sliders ---
  badge: { position: 'absolute', top: '0', right: '0', backgroundColor: '#6c2dff', color: '#FFFFFF', borderRadius: '50%', width: '20px', height: '20px', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #1E293B' },
  tag: { backgroundColor: '#334155', color: '#A78BFA', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' },
  sub: { backgroundColor: '#334155', color: '#94A3B8', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textAlign: 'right' },
  volumeContainer: { position: 'absolute', bottom: -15, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#1E293B', borderRadius: '24px', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #334155', zIndex: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  volumeSlider: { width: '60px', height: '4px', appearance: 'none', backgroundColor: '#334155', borderRadius: '2px', cursor: 'pointer', accentColor: '#6c2dff' },
  volumeBar: { position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(15, 23, 42, 0.8)', borderRadius: '16px', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10, backdropFilter: 'blur(4px)' },
  avatarSlider: { width: '60px', height: '4px', cursor: 'pointer', accentColor: '#6c2dff', borderRadius: '2px' },
  muteIndicator: { position: 'absolute', top: '12px', left: '12px', backgroundColor: '#6c2dff', borderRadius: '50%', padding: '6px', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)' },
  gearBtn: { position: 'absolute', top: '12px', right: '12px', backgroundColor: 'rgba(15, 23, 42, 0.7)', border: '1px solid #334155', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s', backdropFilter: 'blur(4px)' },
  followBtn: { position: 'absolute', bottom: '12px', left: '12px', backgroundColor: '#6c2dff', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', color: '#FFFFFF', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' },
  blockBtn: { position: 'absolute', bottom: '12px', right: '12px', backgroundColor: '#EF4444', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', color: '#F1F5F9', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' },

    premiumFooter: {
        width: '100%',
        backgroundColor: '#0b0f1a',
        borderTop: '1px solid rgba(108, 45, 255, 0.15)',
        padding: '60px 20px 30px 20px',
        marginTop: 'auto',
        boxSizing: 'border-box',
    },
    footerInner: {
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    footerGrid: {
        display: 'grid',
        /* This line handles responsiveness: it stacks columns when they are less than 200px wide */
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '40px',
    },
    footerCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        /* Ensures content centers nicely on very small screens */
        minWidth: '0', 
    },
    brandTitle: {
        fontSize: '24px',
        fontWeight: '800',
        color: '#fff',
        letterSpacing: '-1px',
        margin: '0 0 10px 0',
    },
    brandDesc: {
        fontSize: '14px',
        color: '#94a3b8',
        lineHeight: '1.6',
        margin: '0',
    },
    colTitle: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#fff',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        margin: '0 0 10px 0',
    },
    premiumLink: {
        background: 'none',
        border: 'none',
        color: '#94a3b8',
        fontSize: '14px',
        textAlign: 'left',
        cursor: 'pointer',
        padding: '4px 0',
        transition: '0.2s ease',
        fontFamily: 'inherit',
    },
    socialIcon: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: '0.3s',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#22c55e',
        marginTop: '15px',
    },
    statusDot: {
        width: '8px',
        height: '8px',
        backgroundColor: '#22c55e',
        borderRadius: '50%',
        boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)',
    },
    footerBottom: {
        borderTop: '1px solid rgba(255,255,255,0.05)',
        paddingTop: '25px',
        display: 'flex',
        /* Wraps the copyright and version on small screens */
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '15px',
    },
    copyrightText: {
        fontSize: '13px',
        color: '#64748b',
        margin: '0',
    },
    footerVersion: {
        fontSize: '11px',
        color: '#475569',
        fontWeight: 'bold',
        padding: '4px 10px',
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: '6px',
        letterSpacing: '0.5px',
    }
};